<template>
  <!-- 商品库维护页面: bidding-task-platform 任务 29.2 -->
  <!-- 功能: 商品分页 / 新增 / 编辑 / 逻辑删除 + 嵌套 service_item 抽屉 CRUD -->
  <div style="background:#fff; padding:20px;" v-permission="'app:service-product:list'">
    <!-- 顶部搜索区 -->
    <div style="margin-bottom: 20px">
      <a-form layout="inline">
        <a-form-item label="所属类目">
          <!-- 类目联动下拉, 数据来自 getServiceCategoryPage(pageSize=100) -->
          <a-select
            v-model="query.categoryId"
            placeholder="全部类目"
            allow-clear
            show-search
            option-filter-prop="children"
            style="width: 200px"
          >
            <a-select-option
              v-for="c in categoryOptions"
              :key="c.id"
              :value="c.id"
            >{{ c.name }}</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="商品名称">
          <a-input
            v-model="query.name"
            placeholder="名称模糊匹配"
            allow-clear
            :max-length="100"
            style="width: 200px"
          />
        </a-form-item>
        <a-form-item label="状态">
          <a-select
            v-model="query.status"
            placeholder="全部"
            allow-clear
            style="width: 120px"
          >
            <a-select-option :value="1">启用</a-select-option>
            <a-select-option :value="0">停用</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item>
          <a-button type="primary" icon="search" @click="handleSearch">查询</a-button>
          <a-button style="margin-left:10px" icon="reload" @click="handleReset">重置</a-button>
          <a-button
            type="primary"
            icon="plus"
            style="margin-left:10px"
            v-permission="'app:service-product:list'"
            @click="handleAdd"
          >新增商品</a-button>
        </a-form-item>
      </a-form>
    </div>

    <!-- 列表 -->
    <a-table
      :columns="columns"
      :data-source="data"
      rowKey="id"
      :loading="loading"
      :pagination="pagination"
      @change="handleTableChange"
    >
      <!-- 类目列: 显示 categoryName, 后端未返回时回退到本地 map -->
      <span slot="categoryName" slot-scope="text, record">
        {{ text || categoryNameMap[record.categoryId] || record.categoryId }}
      </span>

      <!-- 主图缩略图: 80x80, 空值占位 -->
      <span slot="mainImageUrl" slot-scope="text">
        <img
          v-if="text"
          :src="text"
          alt="主图"
          style="width:80px; height:80px; object-fit:cover; border:1px solid #f0f0f0;"
        />
        <span v-else style="color:#999">-</span>
      </span>

      <!-- 状态列 -->
      <span slot="status" slot-scope="text">
        <a-tag v-if="text === 1" color="green">启用</a-tag>
        <a-tag v-else-if="text === 0" color="red">停用</a-tag>
        <span v-else>-</span>
      </span>

      <!-- 操作列: 编辑 / 服务项目 / 逻辑删除 -->
      <span slot="action" slot-scope="text, record">
        <a v-permission="'app:service-product:list'" @click="handleEdit(record)">编辑</a>
        <a-divider type="vertical" />
        <a
          v-permission="'app:service-product:list'"
          style="color:#1890ff"
          @click="handleOpenItemDrawer(record)"
        >服务项目</a>
        <a-divider type="vertical" />
        <a-popconfirm title="确定删除该商品吗?删除为逻辑删除,可联系运维恢复" @confirm="handleDelete(record.id)">
          <a v-permission="'app:service-product:list'" style="color: red">删除</a>
        </a-popconfirm>
      </span>
    </a-table>

    <!-- 商品 新增/编辑 弹窗 -->
    <a-modal
      v-model="productVisible"
      :title="productForm.id ? '编辑商品' : '新增商品'"
      :confirm-loading="productSubmitting"
      :mask-closable="false"
      @ok="handleSubmitProduct"
      @cancel="handleCloseProduct"
    >
      <a-form-model
        ref="productFormRef"
        :model="productForm"
        :label-col="{ span: 5 }"
        :wrapper-col="{ span: 16 }"
      >
        <a-form-model-item label="商品名称" required>
          <a-input
            v-model="productForm.name"
            :max-length="100"
            placeholder="1~100 字符"
          />
        </a-form-model-item>
        <a-form-model-item label="所属类目" required>
          <!-- 类目树选择: 一级类目 + 二级类目 -->
          <a-tree-select
            v-model="productForm.categoryId"
            :tree-data="categoryTree"
            placeholder="请选择类目"
            allow-clear
            tree-default-expand-all
            :replace-fields="{ children: 'children', title: 'name', key: 'id', value: 'id' }"
            style="width:100%"
          />
        </a-form-model-item>
        <a-form-model-item label="商品主图">
          <a-upload
            accept="image/*"
            list-type="picture-card"
            :show-upload-list="false"
            :custom-request="handleMainImageUpload"
            :before-upload="beforeMainImageUpload"
            :disabled="mainImageUploading"
          >
            <img
              v-if="productForm.mainImageUrl"
              :src="productForm.mainImageUrl"
              alt="商品主图"
              class="main-image-upload"
            />
            <div v-else>
              <a-icon :type="mainImageUploading ? 'loading' : 'plus'" />
              <div class="ant-upload-text">{{ mainImageUploading ? '上传中' : '上传图片' }}</div>
            </div>
          </a-upload>
          <a-button
            v-if="productForm.mainImageUrl"
            type="link"
            size="small"
            :disabled="mainImageUploading"
            class="remove-main-image-btn"
            @click="handleRemoveMainImage"
          >移除图片</a-button>
          <div class="upload-tip">支持常见图片格式，大小不超过 1MB；点击图片可重新上传。</div>
        </a-form-model-item>
        <a-form-model-item label="状态" required>
          <a-radio-group v-model="productForm.status">
            <a-radio :value="1">启用</a-radio>
            <a-radio :value="0">停用</a-radio>
          </a-radio-group>
        </a-form-model-item>
      </a-form-model>
    </a-modal>

    <!-- 服务项目 抽屉 -->
    <a-drawer
      :title="`服务项目 - ${currentProduct ? currentProduct.name : ''}`"
      :visible="itemDrawerVisible"
      width="720"
      :body-style="{ paddingBottom: '60px' }"
      @close="handleCloseItemDrawer"
    >
      <div style="margin-bottom: 16px;">
        <a-button
          type="primary"
          icon="plus"
          v-permission="'app:service-product:list'"
          @click="handleAddItem"
        >新增服务项目</a-button>
        <a-button
          style="margin-left:10px"
          icon="reload"
          @click="loadItemList"
        >刷新</a-button>
      </div>

      <a-table
        :columns="itemColumns"
        :data-source="itemData"
        rowKey="id"
        :loading="itemLoading"
        :pagination="false"
        size="small"
      >
        <span slot="estimatePrice" slot-scope="text">
          <span v-if="text !== null && text !== undefined && text !== ''">¥ {{ text }}</span>
          <span v-else style="color:#999">-</span>
        </span>

        <span slot="itemAction" slot-scope="text, record">
          <a v-permission="'app:service-product:list'" @click="handleEditItem(record)">编辑</a>
          <a-divider type="vertical" />
          <a-popconfirm title="确定删除该服务项目吗?" @confirm="handleDeleteItem(record.id)">
            <a v-permission="'app:service-product:list'" style="color: red">删除</a>
          </a-popconfirm>
        </span>
      </a-table>

      <!-- 服务项目 新增/编辑 弹窗 -->
      <a-modal
        v-model="itemVisible"
        :title="itemForm.id ? '编辑服务项目' : '新增服务项目'"
        :confirm-loading="itemSubmitting"
        :mask-closable="false"
        @ok="handleSubmitItem"
        @cancel="handleCloseItem"
      >
        <a-form-model
          ref="itemFormRef"
          :model="itemForm"
          :label-col="{ span: 5 }"
          :wrapper-col="{ span: 16 }"
        >
          <a-form-model-item label="项目名称" required>
            <a-input
              v-model="itemForm.name"
              :max-length="50"
              placeholder="1~50 字符"
            />
          </a-form-model-item>
          <a-form-model-item label="预估价">
            <a-input-number
              v-model="itemForm.estimatePrice"
              :min="0.01"
              :max="999999.99"
              :precision="2"
              :step="0.01"
              placeholder="可空,[0.01, 999999.99]"
              style="width:100%"
            />
            <div style="color:#999; font-size:12px;">
              留空表示由商家发布任务时手动填写单价
            </div>
          </a-form-model-item>
        </a-form-model>
      </a-modal>
    </a-drawer>
  </div>
</template>

<script>
// 商品库维护页面
// 沿用 audit / financial-flow 骨架: 顶部搜索 + a-table + a-modal,
// 在此基础上叠加「服务项目」抽屉,在抽屉内做 service_item 的 CRUD.
// 任务 29.2 / Requirements: 19.1-19.9
import {
  getServiceProductPage,
  createServiceProduct,
  updateServiceProduct,
  deleteServiceProduct,
  getServiceCategoryPage,
  getServiceItemList,
  createServiceItem,
  updateServiceItem,
  deleteServiceItem,
  uploadCommonFile
} from '@/api'
import { retreatPageAfterDelete } from '@/utils/pagination'

export default {
  name: 'ServiceProductView',
  data() {
    return {
      // ============== 商品列表 ==============
      data: [],
      loading: false,
      query: {
        categoryId: undefined,
        name: '',
        status: undefined
      },
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0,
        showTotal: (total) => `共 ${total} 条`
      },
      columns: [
        { title: 'ID', dataIndex: 'id', width: 80 },
        {
          title: '所属类目',
          dataIndex: 'categoryName',
          width: 140,
          scopedSlots: { customRender: 'categoryName' }
        },
        { title: '商品名称', dataIndex: 'name' },
        {
          title: '主图',
          dataIndex: 'mainImageUrl',
          width: 110,
          scopedSlots: { customRender: 'mainImageUrl' }
        },
        {
          title: '状态',
          dataIndex: 'status',
          width: 90,
          scopedSlots: { customRender: 'status' }
        },
        { title: '创建时间', dataIndex: 'createdAt', width: 170 },
        {
          title: '操作',
          key: 'action',
          width: 220,
          scopedSlots: { customRender: 'action' }
        }
      ],

      // ============== 类目下拉(联动) ==============
      // categoryOptions: 扁平数组, 给搜索条与列表回填 categoryName 用
      // categoryTree:    树形数组, 给商品弹窗 a-tree-select 用
      categoryOptions: [],
      categoryTree: [],
      categoryNameMap: {},

      // ============== 商品 新增/编辑 ==============
      productVisible: false,
      productSubmitting: false,
      mainImageUploading: false,
      productForm: {
        id: null,
        name: '',
        categoryId: undefined,
        mainImageUrl: '',
        status: 1
      },

      // ============== 服务项目 抽屉 ==============
      itemDrawerVisible: false,
      currentProduct: null,
      itemData: [],
      itemLoading: false,
      itemColumns: [
        { title: 'ID', dataIndex: 'id', width: 80 },
        { title: '项目名称', dataIndex: 'name' },
        {
          title: '预估价',
          dataIndex: 'estimatePrice',
          width: 140,
          scopedSlots: { customRender: 'estimatePrice' }
        },
        {
          title: '操作',
          key: 'itemAction',
          width: 160,
          scopedSlots: { customRender: 'itemAction' }
        }
      ],

      // ============== 服务项目 新增/编辑 ==============
      itemVisible: false,
      itemSubmitting: false,
      itemForm: {
        id: null,
        name: '',
        estimatePrice: undefined
      }
    }
  },
  created() {
    // 优先拉类目下拉(列表里需要 categoryNameMap 回填), 拉完再加载列表
    this.loadCategoryOptions().finally(() => {
      this.loadData()
    })
  },
  methods: {
    // ============== 类目下拉 ==============
    // 取 pageSize=100 的全量类目, 同时构造扁平数组与一级/二级树形结构
    loadCategoryOptions() {
      return getServiceCategoryPage({ page: 1, pageSize: 100 })
        .then((res) => {
          const payload = (res && res.data) || {}
          const list = payload.records || (Array.isArray(payload) ? payload : [])
          this.categoryOptions = list
          // 回填 map
          const map = {}
          list.forEach((c) => { map[c.id] = c.name })
          this.categoryNameMap = map
          // 构造树: parentId=0 视作一级, 其余按 parentId 挂到一级下
          const roots = []
          const childrenMap = {}
          list.forEach((c) => {
            if (!c.parentId || c.parentId === 0) {
              roots.push({ ...c, children: [] })
            } else {
              if (!childrenMap[c.parentId]) childrenMap[c.parentId] = []
              childrenMap[c.parentId].push({ ...c })
            }
          })
          roots.forEach((r) => {
            r.children = childrenMap[r.id] || []
          })
          this.categoryTree = roots
        })
        .catch(() => {
          // 类目拉取失败不阻断主表加载, 仅给出提示
          this.$message.warning('类目下拉数据加载失败,商品页将不显示类目名')
        })
    },

    // ============== 商品 列表 ==============
    loadData() {
      this.loading = true
      const params = {
        page: this.pagination.current,
        pageSize: this.pagination.pageSize,
        categoryId: this.query.categoryId,
        name: this.query.name ? this.query.name.trim() : undefined,
        status: this.query.status
      }
      getServiceProductPage(params)
        .then((res) => {
          const payload = (res && res.data) || {}
          if (payload.records) {
            this.data = payload.records
            this.pagination.total = payload.total || 0
          } else if (Array.isArray(payload)) {
            this.data = payload
            this.pagination.total = payload.length
          } else {
            this.data = []
            this.pagination.total = 0
          }
        })
        .catch((err) => {
          this.$message.error(err && err.message ? err.message : '加载失败')
        })
        .finally(() => {
          this.loading = false
        })
    },
    handleSearch() {
      this.pagination.current = 1
      this.loadData()
    },
    handleReset() {
      this.query = { categoryId: undefined, name: '', status: undefined }
      this.pagination.current = 1
      this.loadData()
    },
    handleTableChange(p) {
      this.pagination.current = p.current
      this.pagination.pageSize = p.pageSize
      this.loadData()
    },

    // ============== 商品 新增/编辑/删除 ==============
    handleAdd() {
      this.productForm = {
        id: null,
        name: '',
        categoryId: undefined,
        mainImageUrl: '',
        status: 1
      }
      this.mainImageUploading = false
      this.productVisible = true
    },
    handleEdit(record) {
      this.productForm = {
        id: record.id,
        name: record.name,
        categoryId: record.categoryId,
        mainImageUrl: record.mainImageUrl || '',
        status: record.status
      }
      this.mainImageUploading = false
      this.productVisible = true
    },
    handleCloseProduct() {
      if (this.mainImageUploading) return
      this.productVisible = false
    },
    beforeMainImageUpload(file) {
      const isImage = file && /^image\//.test(file.type || '')
      if (!isImage) {
        this.$message.error('只能上传图片文件')
        return false
      }
      if (file.size / 1024 / 1024 > 1) {
        this.$message.error('图片大小不能超过 1MB')
        return false
      }
      return true
    },
    handleMainImageUpload(options) {
      this.mainImageUploading = true
      uploadCommonFile(options.file)
        .then((res) => {
          const imageUrl = res && res.data
          if (!imageUrl) throw new Error('上传接口未返回图片地址')
          this.productForm.mainImageUrl = imageUrl
          if (typeof options.onSuccess === 'function') options.onSuccess(res)
          this.$message.success('图片上传成功')
        })
        .catch((err) => {
          if (typeof options.onError === 'function') options.onError(err)
          this.$message.error(err && err.message ? err.message : '图片上传失败')
        })
        .finally(() => {
          this.mainImageUploading = false
        })
    },
    handleRemoveMainImage() {
      this.productForm.mainImageUrl = ''
    },
    handleSubmitProduct() {
      const name = (this.productForm.name || '').trim()
      if (!name) {
        this.$message.error('请输入商品名称')
        return
      }
      if (name.length > 100) {
        this.$message.error('商品名称长度不能超过 100')
        return
      }
      if (!this.productForm.categoryId) {
        this.$message.error('请选择所属类目')
        return
      }
      if (this.productForm.mainImageUrl && this.productForm.mainImageUrl.length > 500) {
        this.$message.error('主图地址长度不能超过 500')
        return
      }
      if (this.productForm.status !== 0 && this.productForm.status !== 1) {
        this.$message.error('状态非法')
        return
      }
      this.productSubmitting = true
      const body = {
        name,
        categoryId: this.productForm.categoryId,
        mainImageUrl: this.productForm.mainImageUrl || null,
        status: this.productForm.status
      }
      const fn = this.productForm.id
        ? updateServiceProduct(this.productForm.id, body)
        : createServiceProduct(body)
      fn.then(() => {
        this.$message.success('保存成功')
        this.productVisible = false
        this.loadData()
      })
        .catch((err) => {
          this.$message.error(err && err.message ? err.message : '保存失败')
        })
        .finally(() => {
          this.productSubmitting = false
        })
    },
    handleDelete(id) {
      deleteServiceProduct(id)
        .then(() => {
          this.$message.success('删除成功')
          retreatPageAfterDelete(this.pagination, this.data.length)
          this.loadData()
        })
        .catch((err) => {
          this.$message.error(err && err.message ? err.message : '删除失败')
        })
    },

    // ============== 服务项目 抽屉 ==============
    handleOpenItemDrawer(record) {
      this.currentProduct = record
      this.itemDrawerVisible = true
      this.loadItemList()
    },
    handleCloseItemDrawer() {
      this.itemDrawerVisible = false
      this.currentProduct = null
      this.itemData = []
    },
    loadItemList() {
      if (!this.currentProduct) return
      this.itemLoading = true
      getServiceItemList({ productId: this.currentProduct.id })
        .then((res) => {
          const payload = (res && res.data) || []
          // 后端返回未删除项数组(无分页)
          if (Array.isArray(payload)) {
            this.itemData = payload
          } else if (payload.records) {
            this.itemData = payload.records
          } else if (payload.list) {
            this.itemData = payload.list
          } else {
            this.itemData = []
          }
        })
        .catch((err) => {
          this.$message.error(err && err.message ? err.message : '服务项目加载失败')
        })
        .finally(() => {
          this.itemLoading = false
        })
    },

    // ============== 服务项目 新增/编辑/删除 ==============
    handleAddItem() {
      this.itemForm = { id: null, name: '', estimatePrice: undefined }
      this.itemVisible = true
    },
    handleEditItem(record) {
      this.itemForm = {
        id: record.id,
        name: record.name,
        estimatePrice: record.estimatePrice === null || record.estimatePrice === undefined
          ? undefined
          : Number(record.estimatePrice)
      }
      this.itemVisible = true
    },
    handleCloseItem() {
      this.itemVisible = false
    },
    handleSubmitItem() {
      if (!this.currentProduct) {
        this.$message.error('未指定所属商品')
        return
      }
      const name = (this.itemForm.name || '').trim()
      if (!name) {
        this.$message.error('请输入项目名称')
        return
      }
      if (name.length > 50) {
        this.$message.error('项目名称长度不能超过 50')
        return
      }
      // 预估价: 允许空; 非空时校验区间与 2 位小数
      let estimatePrice = this.itemForm.estimatePrice
      if (estimatePrice === '' || estimatePrice === undefined || estimatePrice === null) {
        estimatePrice = null
      } else {
        const num = Number(estimatePrice)
        if (Number.isNaN(num)) {
          this.$message.error('预估价格式非法')
          return
        }
        if (num < 0.01 || num > 999999.99) {
          this.$message.error('预估价范围 [0.01, 999999.99]')
          return
        }
        // 限制最多 2 位小数
        if (Math.round(num * 100) !== num * 100) {
          this.$message.error('预估价最多 2 位小数')
          return
        }
        estimatePrice = num
      }

      this.itemSubmitting = true
      const body = {
        productId: this.currentProduct.id,
        name,
        estimatePrice
      }
      const fn = this.itemForm.id
        ? updateServiceItem(this.itemForm.id, body)
        : createServiceItem(body)
      fn.then(() => {
        this.$message.success('保存成功')
        this.itemVisible = false
        this.loadItemList()
      })
        .catch((err) => {
          this.$message.error(err && err.message ? err.message : '保存失败')
        })
        .finally(() => {
          this.itemSubmitting = false
        })
    },
    handleDeleteItem(id) {
      deleteServiceItem(id)
        .then(() => {
          this.$message.success('删除成功')
          this.loadItemList()
        })
        .catch((err) => {
          this.$message.error(err && err.message ? err.message : '删除失败')
        })
    }
  }
}
</script>

<style scoped>
.main-image-upload {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.remove-main-image-btn {
  height: auto;
  padding: 0;
  margin-top: 4px;
  color: #f5222d;
}
.upload-tip {
  color: #999;
  font-size: 12px;
  line-height: 20px;
}
</style>
